// SPDX-License-Identifier: AGPL-3.0-or-later
import type {UserCandidate} from '@app/features/search/state/QuickSwitcherTypes';

export function normalizePersonName(value: string): string {
	return value
		.normalize('NFKD')
		.replace(/\p{M}/gu, '')
		.toLowerCase()
		.replace(/[^\p{L}\p{N}@.]+/gu, ' ')
		.trim();
}

function distance(a: string, b: string): number {
	let row = Array.from({length: b.length + 1}, (_value, i) => i);
	for (let i = 1; i <= a.length; i++) {
		const next = [i];
		for (let j = 1; j <= b.length; j++)
			next[j] = Math.min(next[j - 1] + 1, row[j] + 1, row[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
		row = next;
	}
	return row[b.length];
}

/** Full names win over prefixes; modest typo tolerance is reserved for longer name tokens. */
export function personMatchScore(query: string, values: ReadonlyArray<string>): number {
	const needle = normalizePersonName(query.replace(/^@/, ''));
	if (!needle) return 0;
	const wanted = needle.split(' ');
	return Math.max(
		0,
		...values.map((value) => {
			const name = normalizePersonName(value);
			if (name === needle) return 100;
			if (name.replaceAll(' ', '') === needle.replaceAll(' ', '')) return 95;
			if (name.startsWith(needle)) return 90;
			const words = name.split(' ');
			if (wanted.every((word) => words.some((part) => part.startsWith(word)))) return 80;
			if (name.includes(needle)) return 70;
			const compact = needle.replaceAll(' ', '');
			if (compact.length >= 5 && name.replaceAll(' ', '').includes(compact)) return 65;
			return wanted.every((word) =>
				words.some(
					(part) =>
						part.startsWith(word) ||
						(word.length >= 4 &&
							Math.abs(word.length - part.length) <= 2 &&
							distance(word, part) <= (word.length >= 9 ? 2 : 1)),
				),
			)
				? 40
				: 0;
		}),
	);
}

export function rankPalettePeople(
	candidates: ReadonlyArray<UserCandidate>,
	query: string,
	limit = 6,
): Array<UserCandidate> {
	return candidates
		.map((candidate) => ({
			candidate,
			score: personMatchScore(query, [
				candidate.title,
				candidate.user.globalName ?? '',
				candidate.user.username,
				...candidate.searchValues,
			]),
		}))
		.filter(({score}) => score > 0)
		.sort(
			(a, b) =>
				b.score - a.score ||
				b.candidate.sortWeight - a.candidate.sortWeight ||
				a.candidate.title.localeCompare(b.candidate.title),
		)
		.slice(0, limit)
		.map(({candidate}) => candidate);
}

/** Literal snippets only: never interpret a query as HTML or a regular expression. */
export function highlightSearchText(text: string, query: string): Array<{text: string; match: boolean}> {
	const tokens = query
		.trim()
		.split(/\s+/)
		.filter((word) => word.length >= 2)
		.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
	if (!tokens.length) return [{text, match: false}];
	const pattern = new RegExp(`(${tokens.join('|')})`, 'ig');
	return text
		.split(pattern)
		.map((part, i) => ({text: part, match: i % 2 === 1}))
		.filter((part) => part.text);
}
